import { TableEmptyState } from "@admin-ui/components/composed/table-empty-state";
import { Table, TableCard } from "@admin-ui/components/primitives/application/table/table";
import { Avatar } from "@admin-ui/components/primitives/base/avatar/avatar";
import { Badge, BadgeWithDot } from "@admin-ui/components/primitives/base/badges/badges";
import { accountSetupStatusColors, formatSetupStatus } from "@admin-ui/utils/account-display";
import { formatDate } from "@admin-ui/utils/format-date";
import { formatPrice } from "@admin-ui/utils/format-price";
import { adminAccountPath } from "@travily/routes";
import type {
  AdminListingsListing,
  AdminListingsListingAccountTrait,
  AdminListingsListingDetailsTrait,
  AdminListingsListingPhotosTrait,
  AdminListingsPurchase,
} from "@travily/types/models";
import { ShoppingCart01 } from "@untitledui/icons";
import { Link, TableBody } from "react-aria-components";

type ShowListing = AdminListingsListing &
  AdminListingsListingDetailsTrait &
  AdminListingsListingPhotosTrait &
  AdminListingsListingAccountTrait;

export interface ListingMetaProps {
  listing: ShowListing;
}

export function ListingMeta({ listing }: ListingMetaProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-tertiary">
      <Badge color={listing.listing_type === "Product" ? "brand" : "gray"} size="sm">
        {listing.listing_type}
      </Badge>
      <span>·</span>
      {listing.account ? (
        <Link href={adminAccountPath(listing.account.id)} className="text-brand-600 hover:text-brand-700">
          {listing.account.display_name ?? "Account"}
        </Link>
      ) : (
        <span>—</span>
      )}
      <span>·</span>
      <BadgeWithDot color={accountSetupStatusColors[listing.setup_status]} size="sm" type="pill-color">
        {formatSetupStatus(listing.setup_status)}
      </BadgeWithDot>
    </div>
  );
}

export interface DetailsTabProps {
  listing: ShowListing;
}

export function DetailsTab({ listing }: DetailsTabProps) {
  return (
    <div className="flex flex-col gap-8 pt-8">
      <DetailRow label="Tagline" value={listing.tagline ?? "—"} />
      <DetailRow label="Price" value={formatPrice(listing.price_cents, listing.price_currency, listing.free)} />
      <DetailRow label="Published" value={listing.published_at ? formatDate(listing.published_at) : "Not published"} />
      <DetailRow label="Created" value={formatDate(listing.created_at)} />

      {listing.description && (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-secondary">Description</span>
          <div
            className="prose max-w-none text-sm text-primary"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML is sanitized by Action Text on the server
            dangerouslySetInnerHTML={{ __html: listing.description }}
          />
        </div>
      )}

      {listing.photos.length > 0 && (
        <div className="flex flex-col gap-3">
          <span className="text-sm font-medium text-secondary">Photos</span>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {listing.photos.map((photo) => (
              <div key={photo.id} className="aspect-square overflow-hidden rounded-md bg-secondary">
                {photo.image && (
                  <img
                    src={photo.image.src}
                    srcSet={photo.image.srcset}
                    alt={photo.image.alt ?? ""}
                    className="size-full object-cover"
                    loading="lazy"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium text-secondary">{label}</span>
      <span className="text-sm text-primary">{value}</span>
    </div>
  );
}

export interface PurchasesTabProps {
  purchases: AdminListingsPurchase[];
}

const STATUS_COLORS: Record<AdminListingsPurchase["status"], "success" | "warning" | "error" | "gray"> = {
  completed: "success",
  refunded: "warning",
  failed: "error",
  pending: "gray",
};

export function PurchasesTab({ purchases }: PurchasesTabProps) {
  const purchaseCount = `${purchases.length} ${purchases.length === 1 ? "purchase" : "purchases"}`;

  return (
    <div className="pt-8">
      <TableCard.Root size="sm">
        <TableCard.Header title="Purchases" badge={purchaseCount} />

        <Table aria-label="Purchases">
          <Table.Header>
            <Table.Head id="buyer" label="Buyer" isRowHeader className="w-full max-w-1/3" />
            <Table.Head id="buyer_account" label="Buyer account" />
            <Table.Head id="status" label="Status" />
            <Table.Head id="price" label="Price" />
            <Table.Head id="completed_at" label="Completed" />
            <Table.Head id="created_at" label="Created" />
          </Table.Header>
          <TableBody items={purchases} renderEmptyState={() => <PurchasesEmptyState />}>
            {(purchase: AdminListingsPurchase) => (
              <Table.Row key={purchase.id} id={purchase.id}>
                <Table.Cell>
                  <span className="flex items-center gap-3">
                    <Avatar
                      src={null}
                      alt={purchase.buyer_name ?? purchase.buyer_email}
                      initials={(purchase.buyer_name ?? purchase.buyer_email).charAt(0).toUpperCase()}
                      size="sm"
                    />
                    <span className="flex flex-col">
                      <span className="text-sm font-medium text-primary">{purchase.buyer_name ?? "—"}</span>
                      <span className="text-xs text-tertiary">{purchase.buyer_email}</span>
                    </span>
                  </span>
                </Table.Cell>
                <Table.Cell>
                  {purchase.buyer_account_id ? (
                    <Link
                      href={adminAccountPath(purchase.buyer_account_id)}
                      className="text-sm text-brand-600 hover:text-brand-700"
                    >
                      {purchase.buyer_account_display_name ?? "Account"}
                    </Link>
                  ) : (
                    <span className="text-sm text-tertiary">—</span>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Badge color={STATUS_COLORS[purchase.status]} size="sm" type="pill-color">
                    {purchase.status}
                  </Badge>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  {formatPrice(purchase.price_cents, purchase.price_currency, false)}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  {purchase.completed_at ? formatDate(purchase.completed_at) : "—"}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">{formatDate(purchase.created_at)}</Table.Cell>
              </Table.Row>
            )}
          </TableBody>
        </Table>
      </TableCard.Root>
    </div>
  );
}

function PurchasesEmptyState() {
  return (
    <TableEmptyState icon={ShoppingCart01} title="No purchases yet" description="This listing has no purchases." />
  );
}
